<script setup lang="ts">
/**
 * Admin → Actions. Actions (runbooks) live in the repo as YAML, reviewed via
 * PRs; admins enable them per lineage / target (with a live preview of the
 * resolved targets) and set parameter defaults.
 */
import { ListChecks } from "@lucide/vue";
import { computed, ref } from "vue";
import { useAdminRunbooks, useDatasources } from "@/api/admin";
import AdminActionCard from "@/components/actions/AdminActionCard.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";

definePage({ meta: { title: "Action settings", requires: { adminArea: "runbooks" } } });

const { can } = useSession();
const runbooks = useAdminRunbooks();
const datasources = useDatasources(() => can("admin.datasources"));
const lineages = computed(() => [...new Set((datasources.data.value ?? []).map((d) => d.labels.lineage).filter(Boolean) as string[])].sort());
const q = ref("");
const list = computed(() => {
  const needle = q.value.trim().toLowerCase();
  return (runbooks.data.value ?? []).filter((r) => !needle || [r.name, r.title, r.description, r.targetType, r.capability].some((s) => s.toLowerCase().includes(needle)));
});
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.nav.adminRunbooks" :description="t.admin.runbooksDescription" />
    <ListSkeleton v-if="runbooks.isPending.value" :rows="4" />
    <ErrorState v-else-if="runbooks.error.value" :error="runbooks.error.value" @retry="runbooks.refetch()" />
    <EmptyState
      v-else-if="!runbooks.data.value?.length"
      :icon="ListChecks"
      title="No actions in this build"
      description="Actions are YAML files in the repository's runbooks/ folder, added through reviewed pull requests."
    />
    <template v-else>
      <FilterBar v-model:search="q" :placeholder="t.actions.searchActions" label="Filter actions" class="mb-5" />
      <EmptyState v-if="list.length === 0" :icon="ListChecks" :title="t.common.noResults" />
      <div v-else class="flex flex-col gap-5">
        <AdminActionCard v-for="rb in list" :key="rb.name" :action="rb" :lineages="lineages" />
      </div>
    </template>
  </div>
</template>
